// pages/landing.ts — hero + how-it-works + mission quote + closing CTA.
// Ported 1:1 from frontend2/components/Public.jsx :: LandingPage.

import type { Page, PageContext } from '../router';
import { html, arrowR } from '../util';

const STATS: Array<[string, string]> = [
  ['$38,500', 'Awarded in 2024'],
  ['23',      'Grants funded last year'],
  ['$250K+',  'Awarded since 1995'],
  ['4 wks',   'Average review time'],
];

const STEPS: Array<[string, string]> = [
  ['Check eligibility',
   'Your organization must be a registered 501(c)(3) serving our region. About two minutes to confirm.'],
  ['Create an account',
   'Sign in with Google. The account saves your draft and the history of your applications.'],
  ['Tell us your story',
   'A short three-step form: project details, supporting documents, a final review. Roughly thirty minutes.'],
  ['A real person reads it',
   'Two volunteers read every application. Decisions arrive in about four weeks, with feedback either way.'],
];

function renderStats(): string {
  return STATS.map(([value, label]) => html`
    <div class="d-flex flex-column" style="gap: 8px;">
      <div class="serif" style="font-size: 44px; line-height: 1; color: var(--primary);">${value}</div>
      <div class="t-small" style="max-width: 160px;">${label}</div>
    </div>
  `).join('');
}

function renderSteps(): string {
  return STEPS.map(([title, body], i) => html`
    <div class="d-flex flex-column" style="gap: 14px;">
      <div class="t-small" style="color: var(--primary); font-weight: 500;">${String(i + 1).padStart(2, '0')}</div>
      <h3 class="t-h4 serif" style="margin: 0;">${title}</h3>
      <p class="t-body" style="margin: 0; font-size: 15.5px;">${body}</p>
    </div>
  `).join('');
}

const LandingPage: Page = {
  render(_ctx: PageContext): string {
    return html`
      <!-- HERO -->
      <section style="padding: 80px 0 96px;">
        <div class="wrap">
          <div style="display: grid; grid-template-columns: 1.4fr 1fr; gap: 80px; align-items: end;">
            <div class="reveal">
              <h1 class="t-h1" style="margin: 0; max-width: 920px;">
                Small grants for the quiet, careful work that builds a community.
              </h1>
              <p class="t-lede" style="max-width: 560px; margin-top: 32px;">
                Since 1995, our family has funded the local nonprofits doing the patient, neighborly work that keeps a place together — $500 to $10,000 at a time.
              </p>
              <div class="d-flex" style="gap: 16px; margin-top: 48px; flex-wrap: wrap;">
                <a class="btn btn-primary" data-nav="grant-info">
                  Apply for a grant ${{ __raw: arrowR() }}
                </a>
                <a class="btn btn-outline-primary" data-nav="about">
                  About the foundation
                </a>
              </div>
            </div>
            <div class="reveal reveal-d2" style="position: relative; aspect-ratio: 4/5;">
              <img src="assets/img/landing-hero.jpg"
                   alt="An older couple tending tomato plants together in a community garden at golden hour"
                   class="brand-img"
                   style="position: absolute; inset: 0; width: 100%; height: 100%;"/>
            </div>
          </div>

          <!-- Hero stats -->
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 48px;
                      margin-top: 96px; padding-top: 48px; border-top: 1px solid var(--rule);">
            ${{ __raw: renderStats() }}
          </div>
        </div>
      </section>

      <!-- HOW IT WORKS -->
      <section class="divide divide-soft">
        <div class="wrap">
          <div style="max-width: 680px; margin-bottom: 64px;">
            <div class="eyebrow">How a grant works</div>
            <h2 class="t-h2" style="margin: 16px 0 0;">
              A small process, designed to fit a small organization's time.
            </h2>
          </div>
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 32px;">
            ${{ __raw: renderSteps() }}
          </div>
          <div style="margin-top: 64px;">
            <a class="btn btn-link" data-nav="grant-info">
              Read the full application guide ${{ __raw: arrowR() }}
            </a>
          </div>
        </div>
      </section>

      <!-- MISSION -->
      <section class="divide">
        <div class="wrap" style="display: grid; grid-template-columns: 1fr 1.3fr; gap: 96px; align-items: start;">
          <div>
            <div class="eyebrow">Our work</div>
            <h2 class="t-h2" style="margin: 16px 0 0;">
              Rooted in the place we live.
            </h2>
            <p class="t-body" style="font-size: 17px; margin-top: 32px;">
              We believe that the people closest to a problem are the people best positioned to solve it. Our grants are modest by design — small enough that a single neighborhood library, a single pantry, or a single mentoring program can be the whole of an application's impact.
            </p>
            <p class="t-body" style="font-size: 17px;">
              We prefer the organizations larger funders overlook: ones with clear community ties, careful stewardship, and projects whose effects will still be visible in five years.
            </p>
            <div style="margin-top: 48px;">
              <a class="btn btn-outline-primary" data-nav="about">About the foundation</a>
            </div>
          </div>
          <div class="card" style="padding: 40px 40px 36px;">
            <div class="d-flex" style="gap: 16px; align-items: center;">
              <img src="assets/img/landing-trustee-portrait.jpg"
                   alt="Portrait of Michelle B., Trustee"
                   class="brand-img"
                   style="width: 64px; height: 64px; border-radius: 999px;"/>
              <div>
                <div class="serif" style="font-size: 20px;">Michelle B.</div>
                <div class="t-small">Trustee</div>
              </div>
            </div>
            <p class="serif" style="font-size: 26px; line-height: 1.45; margin: 36px 0 0; color: var(--ink);">
              "We read every application personally. Sometimes we have follow-up questions; sometimes we ask to come by for coffee. We always say why if we say no — and we wire the money quickly if we say yes."
            </p>
          </div>
        </div>
      </section>

      <!-- CLOSING CTA -->
      <section class="divide divide-soft">
        <div class="wrap" style="text-align: center; max-width: 760px; margin: 0 auto;">
          <div class="eyebrow" style="display: inline-block;">The next cycle</div>
          <h2 class="t-h2" style="margin: 16px 0 0;">
            Applications are open through August 31.
          </h2>
          <p class="t-lede" style="margin: 24px auto 0;">
            If your work is small, specific, and rooted in the people you serve, we would like to read about it.
          </p>
          <div class="d-flex" style="gap: 12px; margin-top: 48px; justify-content: center; flex-wrap: wrap;">
            <a class="btn btn-primary" data-nav="grant-info">
              Apply for a grant ${{ __raw: arrowR() }}
            </a>
            <a class="btn btn-outline-primary" data-nav="login">
              Continue a draft
            </a>
          </div>
        </div>
      </section>
    `;
  },
};

export default LandingPage;
